import { useId, useRef, useState, type FormEvent } from 'react';
import {
  apiErrorText,
  useMarkCeoDebtSchedulePaidMutation,
  type CeoDebtScheduleItem,
} from '@shared/api';
import { todayIso } from '@shared/lib/localDate';
import { moneyInput, parseMoney } from '@shared/lib/money';
import { Button } from '@shared/ui/Button';
import { Dialog } from '@shared/ui/Dialog';
import { Field, Input, Label } from '@shared/ui/Input';
import { formatDateRu, formatMoney } from '../ceoFormat';
import { markAmountProblem, rowAmounts, type AmountProblem } from './scheduleModel';
import { DialogError, DialogFieldRow, DialogSummary, FieldError, FieldHint } from './styles';

interface MarkPaidDialogProps {
  item: CeoDebtScheduleItem;
  onClose: () => void;
}

function amountProblemText(problem: AmountProblem, left: number): string {
  switch (problem) {
    case 'empty':
      return 'Укажите сумму платежа числом, например 60 000,00';
    case 'zero':
      return 'Сумма платежа должна быть больше нуля';
    case 'over':
      return `Сумма больше остатка ${formatMoney(left)} — укажите не больше остатка`;
  }
}

/**
 * Отметка оплаты платежа по графику — одна для календаря «К оплате» и карточки договора.
 * Сумма по умолчанию — остаток, дата — сегодня, документ необязателен. Сумма прибавляется
 * к уже оплаченному. Окно закрывается только после ответа сервера, ошибка — в окне,
 * введённое остаётся.
 */
export function MarkPaidDialog({ item, onClose }: MarkPaidDialogProps) {
  const { scheduled, paid, left } = rowAmounts(item);
  const [amount, setAmount] = useState(() => moneyInput(left));
  const [date, setDate] = useState(() => todayIso());
  const [reference, setReference] = useState('');
  const [amountError, setAmountError] = useState<string | null>(null);
  const [dateError, setDateError] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [markPaid, { isLoading }] = useMarkCeoDebtSchedulePaidMutation();
  const amountRef = useRef<HTMLInputElement>(null);
  const uid = useId();
  const formId = `${uid}-form`;

  async function submit(e: FormEvent) {
    e.preventDefault();
    if (isLoading) return;
    const sum = parseMoney(amount);
    const problem = markAmountProblem(sum, left);
    setAmountError(problem ? amountProblemText(problem, left) : null);
    setDateError(date ? null : 'Укажите дату оплаты');
    if (problem || !date || sum === null) return;
    setError(null);
    try {
      await markPaid({
        item_id: item.id,
        paid_amount: sum,
        paid_date: date,
        payment_reference: reference.trim() || undefined,
      }).unwrap();
      onClose();
    } catch (err) {
      setError(apiErrorText(err, 'Не удалось отметить оплату.'));
    }
  }

  return (
    <Dialog
      open
      onClose={() => {
        if (!isLoading) onClose();
      }}
      title={`Оплата платежа № ${item.installment_no} на ${formatDateRu(item.due_date)}`}
      initialFocusRef={amountRef}
      footer={
        <>
          <Button type="button" $variant="secondary" onClick={onClose} disabled={isLoading}>
            Отмена
          </Button>
          <Button type="submit" form={formId} disabled={isLoading}>
            {isLoading ? 'Сохраняю…' : 'Отметить оплату'}
          </Button>
        </>
      }
    >
      <DialogSummary>
        {item.creditor_name} · договор № {item.contract_number}
        <br />
        По графику {formatMoney(scheduled)} · оплачено {formatMoney(paid)} · осталось{' '}
        <strong>{formatMoney(left)}</strong>
      </DialogSummary>
      <form id={formId} onSubmit={submit} noValidate>
        <DialogFieldRow>
          <Field>
            <Label htmlFor={`${uid}-amount`}>Сумма платежа, ₽</Label>
            <Input
              ref={amountRef}
              id={`${uid}-amount`}
              type="text"
              inputMode="decimal"
              autoComplete="off"
              value={amount}
              onChange={(e) => {
                setAmount(e.target.value);
                setAmountError(null);
              }}
              onFocus={(e) => e.currentTarget.select()}
              aria-invalid={amountError ? true : undefined}
              aria-describedby={amountError ? `${uid}-amount-error` : undefined}
            />
            {amountError && (
              <FieldError id={`${uid}-amount-error`} role="alert">
                {amountError}
              </FieldError>
            )}
          </Field>
          <Field>
            <Label htmlFor={`${uid}-date`}>Дата оплаты</Label>
            <Input
              id={`${uid}-date`}
              type="date"
              value={date}
              onChange={(e) => {
                setDate(e.target.value);
                setDateError(null);
              }}
              aria-invalid={dateError ? true : undefined}
              aria-describedby={dateError ? `${uid}-date-error` : undefined}
            />
            {dateError && (
              <FieldError id={`${uid}-date-error`} role="alert">
                {dateError}
              </FieldError>
            )}
          </Field>
        </DialogFieldRow>
        <Field>
          <Label htmlFor={`${uid}-reference`}>Документ / основание (необязательно)</Label>
          <Input
            id={`${uid}-reference`}
            type="text"
            maxLength={120}
            placeholder="Например: п/п № 1245"
            value={reference}
            onChange={(e) => setReference(e.target.value)}
            aria-describedby={`${uid}-reference-hint`}
          />
          <FieldHint id={`${uid}-reference-hint`}>
            Без документа в графике будет «основание не указано».
          </FieldHint>
        </Field>
      </form>
      {error && <DialogError role="alert">{error}</DialogError>}
    </Dialog>
  );
}
